import type { UserPermissionWithPath } from 'api';
import { firmSummaryUrl } from 'common/config/urls';
import { PORTFOLIO_VALUATION } from 'common/constants/auth';
import { FUND_PERMISSION_NAME } from 'common/constants/sidebar';
import { COMPANY_NAME_TITLE } from 'common/constants/summary';
import { dashboardCompanyOverviewUrl, WORKBOARD_CURRENT_VALUATIONS_URL } from 'dashboard409a/common/config/urls';
import { getPreviousWorkboardRoute } from 'dashboard409a/utilities/navigation';
import type { CompanySelectorPermission } from 'pages/Auth/components/CompanySelector/CompanySelector.props';
import type { LoginFirm } from 'pages/Auth/components/FirmList';
import { clearAppAutoSelect, getAppAutoSelect, getSafePreviousPortfolioRoute } from 'pages/Auth/utils';
import type { WorkspaceLocationState, WorkspaceRedirectUser } from './useWorkspaceAutoRedirects.types';

interface ResolveWorkspaceAutoRedirectParams {
  hasNotFirmPermissions: boolean;
  isCompanyProduct: boolean;
  locationState?: WorkspaceLocationState | null;
  lowerPermissions: UserPermissionWithPath[];
  resolvedFirmList: LoginFirm[] | null;
  resolvedStandaloneCompaniesPermissions: CompanySelectorPermission[];
  resolvedUser?: WorkspaceRedirectUser;
}

export type WorkspaceAutoRedirectPlan =
  | {
    kind: 'clear-app-auto-select';
  }
  | {
    kind: 'deep-link' | 'portfolio-resume' | 'single-firm' | 'single-company' | 'staff-company';
    target: string;
  }
  | {
    kind: 'lower-permissions';
    permission: UserPermissionWithPath;
    target: string;
  };

const getSelectedLowerPermission = (lowerPermissions: UserPermissionWithPath[]) => {
  const fundPermission = lowerPermissions.find(
    ({ feature_object }) => feature_object?.object_type === FUND_PERMISSION_NAME
  );

  return lowerPermissions.length > 1 && fundPermission ? fundPermission : lowerPermissions[0];
};

export const applyWorkspaceAutoRedirectPlanSideEffects = ({
  fetchCompanyInfo,
  fetchFundInfo,
  markRestrictedUser,
  plan,
}: {
  fetchCompanyInfo: (companyId: number) => void;
  fetchFundInfo: (fundId: number) => void;
  markRestrictedUser: () => void;
  plan: WorkspaceAutoRedirectPlan;
}) => {
  if (plan.kind === 'clear-app-auto-select') {
    clearAppAutoSelect();
    return;
  }

  if (plan.kind !== 'lower-permissions') {
    clearAppAutoSelect();
    return;
  }

  const featureObject = plan.permission.feature_object;

  markRestrictedUser();

  if (featureObject?.object_type === COMPANY_NAME_TITLE) {
    fetchCompanyInfo(featureObject.object_id);
  } else if (featureObject?.object_id) {
    fetchFundInfo(featureObject.object_id);
  }

  clearAppAutoSelect();
};

export const resolveWorkspaceAutoRedirectPlan = ({
  hasNotFirmPermissions,
  isCompanyProduct,
  locationState,
  lowerPermissions,
  resolvedFirmList,
  resolvedStandaloneCompaniesPermissions,
  resolvedUser,
}: ResolveWorkspaceAutoRedirectParams): WorkspaceAutoRedirectPlan | null => {
  const pathname = locationState?.from?.pathname;
  const search = locationState?.from?.search;

  if (pathname && search) {
    return {
      kind: 'deep-link',
      target: `${pathname}${search}`,
    };
  }

  if (!isCompanyProduct && getAppAutoSelect() === PORTFOLIO_VALUATION) {
    const resume = getSafePreviousPortfolioRoute(resolvedUser?.id);

    if (resume) {
      return {
        kind: 'portfolio-resume',
        target: resume,
      };
    }
  }

  if (!isCompanyProduct && !hasNotFirmPermissions && resolvedFirmList?.length === 1) {
    const firmSlug = resolvedFirmList[0]?.slug;

    if (firmSlug) {
      return {
        kind: 'single-firm',
        target: firmSummaryUrl(firmSlug),
      };
    }
  }

  if (isCompanyProduct && resolvedStandaloneCompaniesPermissions.length === 1) {
    const companySlug = resolvedStandaloneCompaniesPermissions[0]?.slug;

    if (companySlug) {
      return {
        kind: 'single-company',
        target: dashboardCompanyOverviewUrl(companySlug),
      };
    }
  }

  if (isCompanyProduct && resolvedUser?.is_staff) {
    return {
      kind: 'staff-company',
      target: getPreviousWorkboardRoute() || WORKBOARD_CURRENT_VALUATIONS_URL,
    };
  }

  if (hasNotFirmPermissions && lowerPermissions.length) {
    const permission = getSelectedLowerPermission(lowerPermissions);
    const target = permission?.feature_object_path;

    if (permission && target) {
      return {
        kind: 'lower-permissions',
        permission,
        target: `/${target}`,
      };
    }
  }

  return null;
};
